// custom-toastr.service.ts
import { Injectable, inject } from '@angular/core';
import { ToastrService, type IndividualConfig } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class CustomToastrService {
  private toastr = inject(ToastrService);

  private readonly baseTimeout = 7000;
  private readonly extraPerToast = 2000;
  private readonly delayBetweenToasts = 1000; // 1 second
  private toastQueue: Promise<void> = Promise.resolve();
  private isFirstToast = true;

  private getToastOptions(): Partial<IndividualConfig> {
    const currentToasts = this.toastr.toasts.length;
    const timeout = this.baseTimeout + currentToasts * this.extraPerToast;

    return { timeOut: timeout };
  }

  private enqueueToast(callback: () => void): void {
    if (this.isFirstToast) {
      // Show immediately
      callback();
      this.isFirstToast = false;
      return;
    }

    // Delay subsequent toasts
    this.toastQueue = this.toastQueue.then(
      () =>
        new Promise<void>(resolve => {
          setTimeout(() => {
            callback();
            resolve();
          }, this.delayBetweenToasts);
        })
    );
  }

  success(message: string, title?: string): void {
    this.enqueueToast(() => {
      this.toastr.success(message, title, this.getToastOptions());
    });
  }

  error(message: string, title?: string): void {
    this.enqueueToast(() => {
      this.toastr.error(message, title, this.getToastOptions());
    });
  }

  info(message: string, title?: string): void {
    this.enqueueToast(() => {
      this.toastr.info(message, title, this.getToastOptions());
    });
  }

  warning(message: string, title?: string): void {
    this.enqueueToast(() => {
      this.toastr.warning(message, title, this.getToastOptions());
    });
  }
}
